import React, { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { MenuRouteItem, MenuRoute } from "./types";
import Image from "next/image";
import "./MobileNavMenu.styles.scss";
import { getInitials } from "@/utils/getInitials";
import NotificationList from "@/components/layout/NotificationList";
import { useAuth } from "@/context/auth-context";
import { isSameRouteNavigation } from "@/components/navigation/isSameRouteNavigation";

interface MobileNavMenuProps {
    menuItems: MenuRouteItem[];
    theme?: string;
    setTheme?: (theme: string) => void;
    profilePic?: string | null;
    picLoading?: boolean;
    onLogoutClick: () => void; // Add this new prop
}

type MenuLevel = {
    label?: string;
    items: (MenuRouteItem | MenuRoute)[];
};

// Extend MenuRouteItem to allow special notification list item
interface NotificationMenuRouteItem extends MenuRouteItem {
    __notificationList?: boolean;
}

// Special notification list item for the mobile menu stack
const NOTIFICATION_LIST_ITEM: NotificationMenuRouteItem = {
    label: "NotificationsList",
    children: [],
    __notificationList: true,
};

export const MobileNavMenu: React.FC<MobileNavMenuProps> = ({
    menuItems,
    theme,
    setTheme,
    profilePic,
    picLoading,
    onLogoutClick, // Add this prop
}) => {
    const [open, setOpen] = useState(false);
    const [stack, setStack] = useState<MenuLevel[]>([{ items: menuItems }]);
    const menuRef = useRef<HTMLDivElement>(null);
    const { userProfile, msalInstance } = useAuth();
    const router = useRouter();

    // Close menu on outside click
    useEffect(() => {
        if (!open) return;
        function handleClick(e: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
                setStack([{ items: menuItems }]);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open, menuItems]);

    // Reset stack when menu closes
    useEffect(() => {
        if (!open) setStack([{ items: menuItems }]);
    }, [open, menuItems]);

    const currentLevel = stack[stack.length - 1];

    function handleParentClick(item: MenuRouteItem | MenuRoute) {
        if (Array.isArray(item.children) && item.children.length > 0) {
            setStack([
                ...stack,
                {
                    label:
                        (item as MenuRouteItem).label ??
                        (item as MenuRoute).name,
                    items: item.children,
                },
            ]);
        }
    }

    function handleBack() {
        if (stack.length > 1) setStack(stack.slice(0, -1));
    }

    function handleNavClick(
        e: React.MouseEvent,
        to?: string,
        label?: string,
        openInNewTab?: boolean
    ) {
        e.preventDefault();
        setOpen(false);
        setStack([{ items: menuItems }]);

        if (label === "Logout") {
            onLogoutClick();
            return;
        }

        if (to && to !== "#") {
            if (openInNewTab) {
                window.open(to, "_blank", "noopener,noreferrer");
                return;
            }
            if (isSameRouteNavigation(to, window.location.href)) {
                return;
            }
            router.push(to);
        }
    }

    function handleLogoutClick() {
        setOpen(false); // Close mobile menu first
        setStack([{ items: menuItems }]); // Reset stack
        onLogoutClick(); // Then trigger logout confirmation
    }

    return (
        <>
            {/* Hamburger icon on the right */}
            <button
                className="mobile-nav-hamburger ms-auto"
                aria-label="Open menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="hamburger-bar" />
                <span className="hamburger-bar" />
                <span className="hamburger-bar" />
            </button>
            {/* Full-screen overlay menu, slides in from right */}
            {open && (
                <div className="mobile-nav-overlay" ref={menuRef}>
                    <div className="mobile-nav-drawer">
                        {/* Profile avatar and theme toggle row */}
                        <div className="mobile-nav-profile-row">
                            <div className="mobile-nav-profile-col mobile-nav-profile-avatar-col">
                                <div className="mobile-nav-avatar">
                                    {picLoading ? (
                                        <div className="mobile-nav-avatar-skeleton" />
                                    ) : profilePic ? (
                                        <Image
                                            src={profilePic}
                                            alt={userProfile?.name || ""}
                                            className="mobile-nav-avatar-img"
                                            width={36}
                                            height={36}
                                        />
                                    ) : (
                                        <div className="mobile-nav-avatar-initials">
                                            {getInitials(userProfile?.name)}
                                        </div>
                                    )}
                                </div>
                                {userProfile?.name && (
                                    <div className="mobile-nav-profile-greeting">
                                        Hi {userProfile.name.split(" ")[0]}
                                    </div>
                                )}
                            </div>
                            <div className="mobile-nav-profile-col mobile-nav-logo">
                                <Image
                                    src="/logo.png"
                                    alt="Logo"
                                    width={72}
                                    height={72}
                                    style={{
                                        objectFit: "contain",
                                        height: 72,
                                        width: "auto",
                                    }}
                                    priority
                                />
                            </div>
                            <div className="mobile-nav-profile-col mobile-nav-theme-col">
                                {theme && setTheme && (
                                    <button
                                        className="mobile-nav-theme-toggle"
                                        aria-label="Toggle theme"
                                        onClick={() =>
                                            setTheme(
                                                theme === "dark"
                                                    ? "light"
                                                    : "dark"
                                            )
                                        }
                                    >
                                        {theme === "dark" ? (
                                            <i className="bi bi-sun-fill"></i>
                                        ) : (
                                            <i className="bi bi-moon-fill"></i>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        {stack.length > 1 && (
                            <>
                                <button
                                    className="mobile-nav-back-top"
                                    onClick={handleBack}
                                >
                                    <span className="mobile-nav-back-icon">
                                        &#8592;
                                    </span>{" "}
                                    <span className="mobile-nav-back-label">
                                        Back
                                    </span>
                                </button>
                                <div className="mobile-nav-divider" />
                            </>
                        )}

                        <ul>
                            {currentLevel.items.map((item) => {
                                if (
                                    (item as NotificationMenuRouteItem)
                                        .__notificationList
                                ) {
                                    return (
                                        <li key="notification-list">
                                            <NotificationList
                                                applicationName="Dispatch IQ"
                                                userEmail={
                                                    userProfile?.email || ""
                                                }
                                                msalInstance={msalInstance}
                                            />
                                        </li>
                                    );
                                }
                                const hasChildren =
                                    Array.isArray(item.children) &&
                                    item.children.length > 0;

                                if ("to" in item && item.to && !hasChildren) {
                                    return (
                                        <li
                                            key={
                                                (item as MenuRouteItem).label ??
                                                (item as MenuRoute).name
                                            }
                                        >
                                            <a
                                                href={item.to}
                                                onClick={(e) =>
                                                    handleNavClick(
                                                        e,
                                                        item.to,
                                                        (item as MenuRouteItem)
                                                            .label ??
                                                            (item as MenuRoute)
                                                                .name,
                                                        (item as MenuRouteItem)
                                                            .openInNewTab ??
                                                            (item as MenuRoute)
                                                                .openInNewTab
                                                    )
                                                }
                                                className="mobile-nav-link"
                                            >
                                                {(item as MenuRouteItem)
                                                    .label ??
                                                    (item as MenuRoute).name}
                                            </a>
                                        </li>
                                    );
                                }
                                if (hasChildren) {
                                    return (
                                        <li
                                            key={
                                                (item as MenuRouteItem).label ??
                                                (item as MenuRoute).name
                                            }
                                        >
                                            <button
                                                className="mobile-nav-link mobile-nav-parent"
                                                onClick={() =>
                                                    handleParentClick(item)
                                                }
                                            >
                                                {(item as MenuRouteItem)
                                                    .label ??
                                                    (item as MenuRoute).name}
                                                <span className="mobile-nav-caret">
                                                    ›
                                                </span>
                                            </button>
                                        </li>
                                    );
                                }
                                return null;
                            })}
                        </ul>

                        {/* 2. Add ServiceNow and Notifications icons below menu list */}
                        <div
                            className="mobile-nav-icons-row"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: 24,
                                margin: "16px 0",
                            }}
                        >
                            {/* ServiceNow Icon (replace with actual link if needed) */}
                            {/* Notifications Icon */}
                            <button
                                type="button"
                                aria-label="Notifications"
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#757575",
                                    fontSize: 24,
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setStack([
                                        ...stack,
                                        {
                                            label: "Notifications",
                                            items: [NOTIFICATION_LIST_ITEM],
                                        },
                                    ]);
                                }}
                            >
                                <i className="bi bi-bell"></i>
                            </button>
                            {/* Profile Menu Icon */}
                            <button
                                type="button"
                                aria-label="Profile Menu"
                                style={{
                                    background: "none",
                                    border: "none",
                                    color: "#757575",
                                    fontSize: 24,
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    setStack([
                                        ...stack,
                                        {
                                            label: "Profile",
                                            items: [
                                                {
                                                    label: "View Profile",
                                                    to: "/profile",
                                                },
                                                {
                                                    label: "Preferences",
                                                    to: "/preferences?fluid=false",
                                                },
                                                {
                                                    label: "Change Password",
                                                    to: "/change-password",
                                                },
                                                { label: "Logout", to: "#" },
                                            ],
                                        },
                                    ]);
                                }}
                            >
                                <i className="bi bi-person-circle"></i>
                            </button>
                        </div>

                        {/* Logout section at bottom */}
                        {userProfile && (
                            <>
                                <div className="mobile-nav-divider" />
                                <div className="mobile-nav-logout-section">
                                    <button
                                        className="mobile-nav-logout-btn"
                                        onClick={handleLogoutClick}
                                        aria-label="Logout"
                                    >
                                        <i className="bi bi-box-arrow-right me-2"></i>
                                        Logout
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
